import {
    EventCardActions,
    EventCardBadge,
    EventCardDate,
    EventCardDetails,
    EventCardDetailsSpacer,
    EventCardImage,
    EventCardLink,
    EventCardLocation,
    EventCardPrice,
    EventCardPromoCode,
    EventCardPromoted,
    EventCardSaveAction,
    EventCardTitle,
    EventCardTracking,
    EventCardUrgencySignals,
    getEventCardLinkProps,
    getFormattedSalesStatus,
    HORIZONTAL_LEFT_CARD_BADGE_STYLE,
    useFormattedLocationString,
    useGetFormattedEventDateTimeString,
    useMinPriceString,
    VerticalEventCard,
    VERTICAL_EVENT_CARD_IMAGE_CONFIG,
} from '@eventbrite/event-renderer';
import React from 'react';
import { DiscoverEventCardProps } from '../../types';
import './DiscoverVerticalSearchEventCardUrgencySignalB.scss';

export const DiscoverVerticalSearchEventCardUrgencySignalB: React.FunctionComponent<
    DiscoverEventCardProps
> = (props) => {
    const {
        event,
        locale,
        isRaised,
        statsigLocationString,
        moreActions,
        affCode = 'ebdssbdestsearch',
        'data-testid': dataTestId,
        isExpansionsLoading,
    } = props;
    const { urgencySignals } = event;

    const linkProps = getEventCardLinkProps({
        affCode: affCode,
        url: event?.url,
        isTargetBlank: true,
        isNoFollow: event.ticketsBy !== 'Eventbrite',
        onClick: (e) => props.onClick?.(e),
    });

    const formattedDateString = useGetFormattedEventDateTimeString({
        startDate: event?.startDate,
        startTime: event?.startTime,
        timezone: event?.timezone,
        repeatingInstanceCount: event?.repeatingInstanceCount,
        locale: locale,
        useUserTimezone: event.isOnlineEvent,
        dateTimeDelimeter: '•',
    });

    const formattedPriceRangeString = useMinPriceString({
        price: event?.minPrice,
        locale: locale,
    });

    const formattedLocationString = useFormattedLocationString({
        venue: event.venue,
        style: 'venue',
    });

    //Only show active Sales Status (sold out etc)
    //For non-repeating events to prevent incorrect signals.
    const formattedSalesStatus = getFormattedSalesStatus(event);

    const eventCardTrackingProps = {
        statsigLocationString: statsigLocationString,
        event,
    };

    return (
        <section
            className="discover-vertical-event-card"
            data-testid={dataTestId}
        >
            <VerticalEventCard isRaised={isRaised}>
                <EventCardTracking {...eventCardTrackingProps}>
                    <EventCardBadge style={HORIZONTAL_LEFT_CARD_BADGE_STYLE}>
                        {formattedSalesStatus}
                    </EventCardBadge>

                    <EventCardLink {...linkProps}>
                        <EventCardImage
                            name={event.name}
                            loading={props.isLazyImage ? 'lazy' : 'eager'}
                            tokens={{
                                ...VERTICAL_EVENT_CARD_IMAGE_CONFIG,
                                '--image-background-color': event.edgeColor,
                            }}
                            src={event.imageUrl}
                        />
                    </EventCardLink>
                    <EventCardDetails
                        space="xxs-spacing"
                        tokens={{
                            '--EventCardDetailsPadding': '8px 8px 16px 12px',
                            '--EventCardDetailsFlexGrow': '2',
                            '--EventCardDetailsPosition': 'relative',
                        }}
                    >
                        {!formattedSalesStatus && (
                            <EventCardUrgencySignals
                                className="discover-vertical-event-card__urgency-signal"
                                urgencySignals={urgencySignals}
                            />
                        )}
                        <EventCardLink {...linkProps}>
                            <EventCardTitle
                                color="neutral-800"
                                className="event-card__clamp-line--two"
                                title={event.name}
                            />
                        </EventCardLink>
                        <EventCardDate
                            color="neutral-800"
                            formattedDateString={formattedDateString}
                            variant="body-md-bold"
                        />
                        <EventCardLocation
                            className="event-card__clamp-line--one"
                            formattedLocationString={formattedLocationString}
                            variant="body-md"
                        />
                        <EventCardDetailsSpacer />
                        <div className="discover-horizontal-event-card__price-wrapper">
                            <EventCardPrice
                                color="neutral-800"
                                isLoading={isExpansionsLoading}
                                isFree={event.isFree}
                                formattedPriceString={formattedPriceRangeString}
                                variant="body-md-bold"
                            />
                            <EventCardPromoCode
                                openDiscount={event.openDiscount}
                                locale={locale}
                            />
                        </div>

                        <EventCardPromoted isPromoted={event.isPromoted} />

                        <EventCardActions
                            tokens={{
                                '--EventCardActionsPosition': 'absolute',
                                '--EventCardActionsTop': '-48px',
                                '--EventCardActionsRight': '8px',
                            }}
                            actionStyleOverride="outline"
                        >
                            <EventCardSaveAction
                                isAuthenticated={!!props.isAuthenticated}
                                savedByYou={event.savedByYou}
                                id={event.id}
                                eventName={event.name}
                                imageUrl={event.imageUrl}
                            />
                            {moreActions}
                        </EventCardActions>
                    </EventCardDetails>
                </EventCardTracking>
            </VerticalEventCard>
        </section>
    );
};
